import { useWorkflow } from "@/features/workflow/api/getWorkflow";
import useWorkflowStore from "@/store/workflow/workflowStore";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useLocation, useParams } from "react-router-dom";
import { useGetFolders } from "../api/getFolders";
import type { UpdateWorkflowDTO } from "../api/updateWorkflow";
import { useUpdateWorkflow } from "../api/updateWorkflow";

export const useWorkflowData = () => {
  const { workflow_id } = useParams<{
    workflow_id: string;
  }>();
  const location = useLocation();
  // ワークフロー名は初期値を空にし、必要なタイミングでのみ更新
  const [workflowName, setWorkflowName] = useState("");
  const [isWorkflowNameEditing, setIsWorkflowNameEditing] = useState(false);
  const [isWorkflowNameError, setIsWorkflowNameError] = useState(false);
  const queryClient = useQueryClient();
  const { workflows, setWorkflow } = useWorkflowStore();
  const [formatRuleId, setFormatRuleId] = useState<string>("");
  // ワークフローIDの変更を追跡
  const prevWorkflowIdRef = useRef<string | undefined>(workflow_id);
  // 初期化済みかどうかのフラグ
  const initializedRef = useRef<boolean>(false);
  // パス変更の追跡用
  const prevPathRef = useRef<string>(location.pathname);

  // APIクエリ
  const { data: workFlow, refetch } = useWorkflow({
    workflow_id: workflow_id ? workflow_id : "",
  });

  // フォルダデータの取得
  const { data: folderList, refetch: refetchFolders } = useGetFolders({});

  // フォルダに属さないワークフローの一覧
  const unassignedWorkflows = folderList?.unassigned_workflows ?? [];
  // フォルダ一覧（各フォルダに属するワークフローを含む）
  const folders = folderList?.folders ?? [];

  // 現在のワークフローを取得するヘルパー関数
  const getCurrentWorkflow = useCallback(() => {
    if (!workflow_id || !folderList) return null;

    return (
      unassignedWorkflows.find((w) => w.id === workflow_id) ||
      folders.flatMap((f) => f.workflows).find((w) => w.id === workflow_id)
    );
  }, [workflow_id, unassignedWorkflows, folders, folderList]);

  // ワークフロー名を初期化する関数
  const initializeWorkflowName = useCallback(() => {
    const currentWorkflow = getCurrentWorkflow();
    if (currentWorkflow) {
      setWorkflowName(currentWorkflow.workflow_name);
    } else {
      setWorkflowName("");
    }
    initializedRef.current = true;
  }, [getCurrentWorkflow]);

  // ワークフローIDの変更と初期化処理
  const handleWorkflowIdChange = useCallback(() => {
    // ワークフローIDが変わった場合
    if (workflow_id !== prevWorkflowIdRef.current) {
      // 編集状態をリセット
      setIsWorkflowNameEditing(false);
      setIsWorkflowNameError(false);

      // 初期化フラグをリセット
      initializedRef.current = false;

      // 前回のワークフローIDを更新
      prevWorkflowIdRef.current = workflow_id;
    }

    // 初期化されていなくて、フォルダデータがある場合のみ初期化
    if (!initializedRef.current && folderList) {
      initializeWorkflowName();
    }
  }, [workflow_id, folderList, initializeWorkflowName]);

  // フォーマットルールの設定
  const updateFormatRuleId = useCallback(() => {
    if (workFlow) {
      if (workFlow.format_rules && workFlow.format_rules.length > 0) {
        setFormatRuleId(workFlow.format_rules[0]?.id ?? "");
      } else {
        setFormatRuleId("");
      }
    } else {
      setFormatRuleId("");
    }
  }, [workFlow]);

  // URLパス変更時の編集状態リセット
  const resetEditStateOnPathChange = useCallback(() => {
    if (location.pathname !== prevPathRef.current) {
      setIsWorkflowNameEditing(false);
      setIsWorkflowNameError(false);
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Zustandストアの更新
  const updateWorkflowStore = useCallback(() => {
    if (folderList) {
      // 全ワークフローを結合
      const allWorkflows = [
        ...folderList.unassigned_workflows,
        ...folderList.folders.flatMap((folder) => folder.workflows),
      ];

      // ストア更新（ワークフロー名は更新しない）
      setWorkflow(allWorkflows);
    }
  }, [folderList, setWorkflow]);

  useEffect(() => {
    handleWorkflowIdChange();
    updateFormatRuleId();
    resetEditStateOnPathChange();
    updateWorkflowStore();
  }, [
    workflow_id,
    workFlow,
    folderList,
    location.pathname,
    handleWorkflowIdChange,
    updateFormatRuleId,
    resetEditStateOnPathChange,
    updateWorkflowStore,
  ]);

  const useUpdateWorkflowMutation = useUpdateWorkflow({});

  // 編集開始
  const handleEditWorkflowName = () => {
    if (!workflow_id) {
      return;
    }
    setIsWorkflowNameEditing(true);
  };

  // 保存処理
  const handleSaveWorkflowName = async () => {
    if (!workflow_id) return;

    if (workflowName.trim() === "") {
      setIsWorkflowNameError(true);
      return;
    }

    // 入力された名前を保持
    const newWorkflowName = workflowName;

    const updateWorkflowDTO: UpdateWorkflowDTO = {
      workflow_id,
      data: {
        workflow_name: newWorkflowName,
      },
    };

    try {
      // 現在のワークフロー名を取得
      const currentWorkflow = getCurrentWorkflow();

      if (
        currentWorkflow &&
        newWorkflowName !== currentWorkflow.workflow_name
      ) {
        // 編集モードを終了（ワークフロー名はそのまま）
        setIsWorkflowNameEditing(false);

        // APIを呼び出して保存
        await useUpdateWorkflowMutation.mutateAsync(updateWorkflowDTO, {
          onSuccess: () => {
            // クエリキャッシュを無効化
            queryClient.invalidateQueries(["folders"]);
            queryClient.invalidateQueries(["Workflows"]);
            queryClient.invalidateQueries(["workflow", workflow_id]);

            // フォルダリストを再取得（ワークフロー名のステートは変更しない）
            refetchFolders();

            // Zustandストアも更新
            if (workflows.length > 0) {
              const updatedWorkflows = [...workflows];
              const workflowIndex = updatedWorkflows.findIndex(
                (w) => w.id === workflow_id
              );
              if (
                workflowIndex !== -1 &&
                updatedWorkflows[workflowIndex] !== undefined
              ) {
                updatedWorkflows[workflowIndex].workflow_name = newWorkflowName;
                setWorkflow(updatedWorkflows);
              }
            }

            // データの再取得（ワークフロー名のステートは変更しない）
            refetch();
          },
        });
      } else {
        // 変更がない場合は編集モードだけ終了
        setIsWorkflowNameEditing(false);
      }
    } catch (error) {
      setIsWorkflowNameEditing(false);
    }
  };

  const handleWorkflowNameKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    // NOTE: IME変換中のEnterキー押下を無視するため、isComposingをチェック
    if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      handleSaveWorkflowName();
    }
  };

  // 編集状態をリセット
  const resetEditState = () => {
    setIsWorkflowNameEditing(false);
    setIsWorkflowNameError(false);
  };

  return {
    workflow_id,
    workflowName,
    isWorkflowNameEditing,
    setIsWorkflowNameEditing,
    isWorkflowNameError,
    formatRuleId,
    setWorkflowName,
    handleEditWorkflowName,
    handleSaveWorkflowName,
    handleWorkflowNameKeyDown,
    setIsWorkflowNameError,
    workFlow: workFlow ?? null,
    refetch,
    unassignedWorkflows,
    folders,
    resetEditState,
  };
};
